import { Badge, Popover, Progress } from 'antd';
import TooltipComponent from 'react-tooltip-lite';
import React from 'react';
import { Link } from 'react-router-dom';
import CoordiChart from '../../../../components/CoordiChart';
import { getFormattedDate } from '../../../../services/util';
import { dateFormat_DD_MM_YY } from '../../../../util/GlobalConstant';
import ScoreHistoryTable from './HistoryTable';
import style from './index.scss';

import { orderBy } from '../../../../util/lodashlike';
export const CLUSTER_DATA_KEYS = {
  xKey: 'severityScore',
  yKey: 'yieldRate',
  zKey: 'clusterSize'
};

const getClusterColor = (quadrant) => {
  const colors = {
    1: '#df6057',
    2: '#e29840',
    3: '#71c969',
    4: '#3e7ad3'
  };

  return colors[quadrant] || '#71C969';
};

const getCustomerTypeString = (customerType) => {
  switch (customerType) {
    case 'I':
      return 'Individual';
    case 'C':
      return 'Corporate';
    default:
      return '--';
  }
};

const getCustomerTypeIconClass = (customerType) => {
  switch (customerType) {
    case 'I':
      return 'fa fa-user';
    case 'C':
      return 'fa fa-building';
    default:
      return '';
  }
};

export const getClusterQuadrant = (
  cluster,
  convertToPercentage,
  xKey,
  yKey
) => {
  if (cluster) {
    let x, y;

    if (xKey) {
      x = cluster[xKey];
    } else {
      x = cluster.x;
    }

    if (yKey) {
      y = cluster[yKey];
    } else {
      y = cluster.y;
    }

    // Q1
    if (
      x * (convertToPercentage ? 100 : 1) >= 50 &&
      y * (convertToPercentage ? 100 : 1) >= 50
    ) {
      return 1;
    }

    // Q2
    if (
      x * (convertToPercentage ? 100 : 1) <= 50 &&
      y * (convertToPercentage ? 100 : 1) >= 50
    ) {
      return 2;
    }

    // Q3
    if (
      x * (convertToPercentage ? 100 : 1) <= 50 &&
      y * (convertToPercentage ? 100 : 1) <= 50
    ) {
      return 3;
    }

    // Q4
    if (
      x * (convertToPercentage ? 100 : 1) >= 50 &&
      y * (convertToPercentage ? 100 : 1) <= 50
    ) {
      return 4;
    }
  }

  return null;
};

export const getQuadrantText = (quadrant) => {
  switch (quadrant) {
    case 1:
      return 'High Risk, High Yield';
    case 2:
      return 'Low Risk, High Yield';
    case 3:
      return 'Low Risk, Low Yield';
    case 4:
      return 'High Risk, Low Yield';
    default:
      return null;
  }
};

const getColumnRender = (column, record, getCRSLevelColor) => {
  const history = record.riskScoreHistory;
  switch (column.itemName) {
    case 'customerName':
      return (
        <div className={style.customerNameGroup} key={column.itemName}>
          <Link
            to={{
              pathname: `/app/crs/details/${encodeURIComponent(
                record.customerKey
              )}`
            }}
            target="_blank"
            rel="noopener noreferrer">
            {record.customerName}
          </Link>
          <div className={style[record?.customerStatus?.toLowerCase()]}>
            <span className={style.dot} />
            {record.customerStatus}
          </div>
        </div>
      );
    case 'customerKey':
      return (
        <div className={style.customerKey} key={column.itemName}>
          {record.customerKey}
        </div>
      );
    case 'kycReviewDate':
      return (
        <div className={style.kycReviewDate} key={column.itemName}>
          {record.kycReviewDate
            ? getFormattedDate(record.kycReviewDate, dateFormat_DD_MM_YY)
            : '--'}
        </div>
      );
    case 'customerOnboardDate':
      return (
        <div className={style.customerOnboardDate} key={column.itemName}>
          {getFormattedDate(record.customerOnboardDate, dateFormat_DD_MM_YY)}
        </div>
      );
    case 'productName':
      return <div key={column.itemName}>{record.productName}</div>;
    case 'overallRiskLevel':
      return (
        <div className={style.riskLevel} key={column.itemName}>
          {record.overallRiskLevel}
        </div>
      );
    case 'overallRiskScore':
      return (
        <div className={style.riskScoreGroup} key={column.itemName}>
          <span>{record.overallRiskScore.toFixed(2)}</span>
          <Progress
            className={style.progressBar}
            percent={record.overallRiskScore}
            showInfo={false}
            strokeColor={
              record?.overallRiskLevel &&
              getCRSLevelColor(record?.overallRiskLevel)
            }
          />
        </div>
      );
    case 'riskScoreHistory':
      return (
        <div className={style.riskScoreHistory}>
          <Badge
            size="small"
            count={history.length}
            style={{
              backgroundColor: '#21B4BD',
              visibility: history.length > 0 ? 'unset' : 'hidden'
            }}>
            <Popover
              placement="bottomLeft"
              title="History of Risk Score Changes"
              content={<ScoreHistoryTable scoreHistory={history} />}
              trigger="click">
              <span
                className={style.historyIcon}
                style={{
                  visibility: history.length > 0 ? 'unset' : 'hidden'
                }}
              />
            </Popover>
          </Badge>
        </div>
      );
    case 'kycRiskScore':
      return (
        <div className={style.kycRiskScore} key={column.itemName}>
          {record.kycRiskScore}
        </div>
      );
    case 'cluster': {
      const clusterQuadrant = getClusterQuadrant(
        record,
        false,
        CLUSTER_DATA_KEYS.xKey,
        CLUSTER_DATA_KEYS.yKey
      );
      const quadrantText = getQuadrantText(clusterQuadrant);
      const clusterVolume = record[CLUSTER_DATA_KEYS.zKey];
      const alertYield = parseFloat(
        record[CLUSTER_DATA_KEYS.yKey]
      ).toFixedNoRounding(2);

      return (
        <div className={style.clusterGroup} key={column.itemName}>
          <div>
            <strong>{`Q${clusterQuadrant}`}</strong>
            <span className={style.clusterDescription}>{quadrantText}</span>
            <div className={style.clusterPercentageGroup}>
              <span>{`${clusterVolume}, ${alertYield}%`}</span>
              <Progress
                className={style.progressBar}
                percent={record.overallRiskScore}
                showInfo={false}
                strokeColor={getClusterColor(clusterQuadrant)}
              />
            </div>
          </div>
          <TooltipComponent
            className={style.customTooltipArrow}
            content={
              <div className={style.clusterPopover}>
                <header>Cluster Overview</header>
                <CoordiChart
                  xKey={CLUSTER_DATA_KEYS.xKey}
                  yKey={CLUSTER_DATA_KEYS.yKey}
                  zKey={CLUSTER_DATA_KEYS.zKey}
                  data={[record]}
                  selectedItem={record}
                />
              </div>
            }>
            <div className={style.informationIcon} />
          </TooltipComponent>
        </div>
      );
    }
    case 'customerIndustry':
      return (
        <div className={style.customerIndustry} key={column.itemName}>
          {record.customerIndustry}
        </div>
      );
    case 'customerType':
      return (
        <div className={style.customerType} key={column.itemName}>
          <i className={getCustomerTypeIconClass(record.customerType)}></i>
          <span>{getCustomerTypeString(record.customerType)}</span>
        </div>
      );
    case 'strCount':
      return (
        <div className={style.strCount} key={column.itemName}>
          {record.strCount}
        </div>
      );
    case 'segmentName':
      return <div key={column.itemName}>{record.segmentName}</div>;
    case 'segmentCode':
      return (
        <div className={style.segmentCode} key={column.itemName}>
          {record.segmentCode}
        </div>
      );
    default:
      return <div key={column.itemName}>{record[column.itemName] || '--'}</div>;
  }
};

export const parseColumnsForCustomerListing = (settings, getCRSLevelColor) => {
  let columns = null;
  if (settings) {
    const sortedSettings = orderBy(settings, ['columnIndex'], ['asc']).filter(
      (column) => column.columnVisibility
    );
    columns = sortedSettings.map((setting) => {
      return {
        title: setting.columnName,
        children: [
          {
            title: (
              <>
                {orderBy(setting.columnDetail, ['itemIndex'], ['asc'])
                  .filter((column) => column.visibility)
                  .map((column) => (
                    <p key={column.itemName}>{column.itemLabel}</p>
                  ))}
              </>
            ),
            render: (text, record) => (
              <>
                {orderBy(setting.columnDetail, ['itemIndex'], ['asc'])
                  .filter((column) => column.visibility)
                  .map((column) =>
                    getColumnRender(column, record, getCRSLevelColor)
                  )}
              </>
            )
          }
        ]
      };
    });
  }
  return columns;
};

export const parseCsvHeadersForCustomerListing = (columns) => {
  if (!columns) return [];
  return orderBy(columns, ['columnIndex'], ['asc'])
    .flatMap((column) => orderBy(column.columnDetail, ['itemIndex'], ['asc']))
    .filter((column) => column?.visibility)
    .map((column) => ({
      label: column.itemLabel,
      key: column.itemName
    }));
};
